// swimlaneCells — the HTML grid-cell renderers for the swimlane board: a step
// block, a comment/prose cell, and a terminator pill. Pure presentational
// components: each takes its row + pre-wrapped geometry + handlers as props and
// renders one grid cell; no layout math (heights/lines come from the geometry
// pass, arrows attach to these cells' measured edges elsewhere).

import { useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import type { Row, NodeId, SwimlaneLayout } from "@archlang/engine";
import { BLOCK_INSET, type SwimlaneBlockGeom } from "@archlang/scene/process";
import type { BlockLabel } from "./labelForStep";
import { ContextPad } from "./ContextPad.js";
import { InlineEditInput } from "./InlineEditInput.js";
import { useAppendDrag } from "./useAppendDrag.js";
import type { ProcessStepEditApi } from "./processStepEdit.js";
import type { StepEdgeStyle } from "./swimlaneTypes.js";

export function BlockCell({
  row,
  variant,
  label,
  isNote,
  active,
  reordered,
  isEntry,
  edgeStyle,
  laneIndex,
  topPad,
  bottomPad,
  branchSelected,
  branchLabel,
  reentryTarget,
  trigger,
  wait,
  laneOwnerColor,
  blockGeom,
  selected,
  onClick,
  stepEdit,
  stepNodeId,
  stepIndex,
  stepFrom,
  stepTo,
  stepModifiers,
  onDragHandleDown,
  onBodyDragDown,
  dragging,
}: {
  row: Row;
  variant: SwimlaneLayout["variant"];
  stepEdit?: ProcessStepEditApi;
  stepNodeId?: NodeId;
  stepIndex: number | null;
  stepFrom?: string;
  stepTo?: string;
  /** This call step's current trailing modifiers (`as` / comment) — passed to
   *  the ⋯ menu's modifier chips (P3 #24). */
  stepModifiers?: { readonly asName?: string; readonly comment?: string };
  /** Begin a reorder drag from this step's handle (edit mode, flow only). */
  onDragHandleDown?: (nodeId: NodeId, e: ReactPointerEvent) => void;
  /** Begin a THRESHOLD-gated reorder drag from the cell BODY (P2 #19, "drag by
   *  body"): a click still selects; a press-and-move drags. Guarded against the
   *  cell's own interactive controls (⋮⋮ handle / ⋯ menu / inline inputs). */
  onBodyDragDown?: (nodeId: NodeId, e: ReactPointerEvent) => void;
  /** True while THIS step is the one being dragged — dim it. */
  dragging?: boolean;
  label: BlockLabel;
  isNote: boolean;
  /** "Colour by" tint of the block's (callee) lane module. */
  laneOwnerColor?: string;
  /** Pure geometry for this block — box height + pre-wrapped text lines. */
  blockGeom?: SwimlaneBlockGeom;
  /** v0.9.0 §2.1: this step is one of the process's START triggers (a leading
   *  `await`) — rendered with a start-event marker. */
  trigger?: boolean;
  /** PROCESS.md §6: a MID-FLOW `await` wait (not a leading trigger) — rendered
   *  with a timer/clock marker, distinct from a plain note and the ▶ trigger. */
  wait?: boolean;
  /** Set when this note is a `go <target>` re-entry marker — render it as a
   *  back-reference (`↩ target`) with data-go-target so the board links it to
   *  its `as` step. */
  reentryTarget?: string;
  active: boolean;
  /** Live conformance: this step was observed running out of order vs the model
   *  (the "reordered" drift) — marked with a fuchsia violation ring + ⇄ glyph. */
  reordered?: boolean;
  isEntry: boolean;
  edgeStyle: StepEdgeStyle | undefined;
  laneIndex: (id: string) => number;
  topPad: number;
  bottomPad: number;
  /** True when this step's enclosing branch (and all ancestors) is
   *  the user-selected one. False = unselected alternative — render
   *  faded so the chosen path stays visually dominant. */
  branchSelected?: boolean;
  /** Label of the nearest enclosing branch, surfaced as a small tag
   *  on the block so users can tell which alternative a step belongs
   *  to in the Flow view. */
  branchLabel?: string;
  /** EDIT-MODE multi-select membership (P5 #34) — this step is in the shared
   *  selection. Renders a sky ring/glow so it reads as selected-for-editing,
   *  distinct from the `active` scrubber highlight (active wins visually). */
  selected?: boolean;
  onClick: (e: ReactMouseEvent) => void;
}) {
  void variant;
  void isEntry;
  const toI = laneIndex(row.toLaneId);
  const [editingCallee, setEditingCallee] = useState(false);
  // Tail-append gesture (P2 #20): press the bottom NUB and drag away to spawn
  // the next step. Same OUTCOME as the connector "+" (`addStepAfter`) — the
  // gesture is the difference. Hook runs unconditionally (rules of hooks); the
  // nub itself is only rendered in edit mode on a real call step below.
  const append = useAppendDrag();
  // Double-click a PLAIN call → inline-retype its `owner > callee` (plan §4c).
  // Capability = presence: only offered when the api says the whole-`from > to`
  // rewrite drops nothing (no args/comment/as/bind) — otherwise the callee stays
  // read-only and the ⋯ menu remains the (lossy-aware) path.
  const calleeEditable =
    !!stepEdit?.canEdit &&
    !isNote &&
    stepIndex !== null &&
    !!stepNodeId &&
    !!stepFrom &&
    !!stepTo &&
    stepEdit.canReplaceCall(stepIndex);
  // Capability = presence: the tail-append nub is offered only in edit mode, on
  // a real call step with an addressable node — never on a note/comment cell.
  const appendable = !!stepEdit?.canEdit && !isNote && !!stepNodeId;
  if (toI < 0) return null;
  const widgetColor = !active && edgeStyle?.color ? edgeStyle.color : undefined;
  // A saga compensation (unwind) node — the reverse/undo path. Solid ORANGE
  // (not dashed) so the rollback nodes read as a distinct, definite track.
  const compensation = edgeStyle?.compensation === true;
  // Precedence: active > compensation > "colour by" lane tint > the step's
  // own widget colour > neutral. The unified coloring wins over the widget
  // default so an aspect pick actually reclassifies the diagram.
  const borderColor = isNote
    ? (active ? "#fbbf24" : "#92400e")
    : active ? "#38bdf8" : compensation ? "#f59e0b" : laneOwnerColor ?? widgetColor ?? "#94a3b8";
  const bg = isNote
    ? (active ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.08)")
    : active ? "rgba(56,189,248,0.18)" : compensation ? "rgba(245,158,11,0.12)" : "rgba(15,23,42,0.85)";
  const labelColor = active ? "#7dd3fc" : compensation ? "#fcd9a3" : "#e2e8f0";
  const subtitleColor = active ? "rgba(125,211,252,0.7)" : compensation ? "rgba(245,158,11,0.7)" : "#94a3b8";
  const isDashed = isNote || (!compensation && !!edgeStyle?.dashed);
  return (
    <div
      data-step-index={row.stepIndex ?? undefined}
      data-row-y={row.y}
      data-lane={toI}
      data-compensation-node={compensation ? "true" : undefined}
      data-go-target={reentryTarget || undefined}
      data-reentry={reentryTarget ? "true" : undefined}
      data-trigger={trigger ? "true" : undefined}
      data-wait={wait ? "true" : undefined}
      data-reordered={reordered ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
      aria-selected={selected === undefined ? undefined : selected}
      title={
        reordered
          ? "observed running out of order vs the model (conformance drift)"
          : trigger
            ? "start trigger — the process begins when this fires"
            : wait
              ? "wait — the flow pauses here until this fires"
              : calleeEditable
                ? "double-click to edit the call"
                : undefined
      }
      onClick={onClick}
      onPointerDown={
        onBodyDragDown && stepNodeId && !isNote
          ? (e) => {
              // Don't arm a drag from the cell's own controls (⋮⋮ handle / ⋯ menu /
              // inline inputs / buttons) — they own their gesture. A click still
              // selects; a real drag swallows the trailing click.
              if ((e.target as HTMLElement)?.closest?.("button, a, input, textarea, select, [data-drag-handle], [data-no-drag]")) return;
              onBodyDragDown(stepNodeId, e);
            }
          : undefined
      }
      onDoubleClick={
        calleeEditable
          ? (e) => {
              e.stopPropagation();
              setEditingCallee(true);
            }
          : undefined
      }
      className={`group/cell relative flex items-center justify-center cursor-pointer${dragging ? " opacity-40" : ""}`}
      style={{
        gridRow: row.y + 1,
        gridColumn: toI + 1,
        padding: `${topPad}px ${BLOCK_INSET}px ${bottomPad}px`,
      }}
    >
      <div
        className="w-full text-center"
        style={{
          background: bg,
          border: `${active ? 1.5 : 1}px ${isDashed ? "dashed" : "solid"} ${borderColor}`,
          // Reorder drift: a fuchsia ring layered OVER the border (distinct from
          // the amber compensation track), so it shows through an active step too.
          // A selected-for-edit step (P5 #34) gets a sky ring when it isn't the
          // active/reordered one (those own the outline; active already reads as
          // selected). One outline slot, so reorder > active-selected > selected.
          outline: reordered ? "2px solid #e879f9" : selected && !active ? "2px solid #38bdf8" : undefined,
          outlineOffset: reordered || (selected && !active) ? 1 : undefined,
          borderRadius: 6,
          color: labelColor,
          fontSize: 11,
          lineHeight: "14px",
          padding: "3px 6px",
          // Explicit height from the pure geometry (border-box, matching the
          // wrapped lines rendered below) — the overlay attaches arrows to
          // these exact edges. Fallback keeps the committed min height.
          height: blockGeom?.rect.h,
          minHeight: blockGeom ? undefined : 22,
          boxSizing: "border-box",
          // Unselected branch alternatives in Flow render at reduced
          // opacity so the chosen path stays visually dominant. Active
          // / selected always renders at full opacity.
          opacity: branchSelected === false && !active ? 0.45 : 1,
        }}
      >
        {branchLabel && branchSelected === false && (
          <div
            style={{
              fontSize: 9,
              lineHeight: "11px",
              color: subtitleColor,
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            {branchLabel}
          </div>
        )}
        {/* A `go` re-entry marker reads as `↩ target` (a back-reference), not a
            plain note, so it's visually distinct from a comment. The `↩` form
            comes from labelForStep (shared with the headless export), so the
            geometry's PRE-WRAPPED lines are painted verbatim here (nowrap) and
            the browser can never wrap differently than the pass that sized
            this box. */}
        {editingCallee ? (
          <InlineEditInput
            initial={`${stepFrom} > ${stepTo}`}
            ariaLabel="Edit call (owner > callee)"
            onCommit={(val) => {
              const gt = val.indexOf(">");
              if (gt >= 0 && stepNodeId) {
                stepEdit?.replaceCall(stepNodeId, val.slice(0, gt).trim(), val.slice(gt + 1).trim());
              }
              setEditingCallee(false);
            }}
            onCancel={() => setEditingCallee(false)}
          />
        ) : (
          <div style={reentryTarget ? { color: "#c4b5fd" } : undefined}>
            {(!blockGeom ? [label.primary] : blockGeom.lines).map(
              (ln, li) => (
                <div key={li} style={{ whiteSpace: "nowrap" }}>
                  {li === 0 && reordered && (
                    <span style={{ marginRight: 4, fontSize: 10, color: "#e879f9" }} aria-hidden>⇄</span>
                  )}
                  {li === 0 && trigger && <span style={{ marginRight: 4, fontSize: 9 }}>▶</span>}
                  {li === 0 && !trigger && wait && (
                    // Mid-flow await — a small clock (circle + hand), the live
                    // twin of the composer's drawn timer marker.
                    <svg width={9} height={9} viewBox="0 0 9 9" style={{ marginRight: 4, verticalAlign: "-1px" }} aria-hidden>
                      <circle cx={4.5} cy={4.5} r={3.5} fill="none" stroke="currentColor" strokeWidth={1} />
                      <path d="M 4.5 2.5 V 4.5 L 6.1 5.4" fill="none" stroke="currentColor" strokeWidth={1} />
                    </svg>
                  )}
                  {ln}
                </div>
              ),
            )}
          </div>
        )}
        {label.subtitle && (
          <div
            style={{
              fontSize: 9,
              lineHeight: "11px",
              color: subtitleColor,
              marginTop: 1,
            }}
          >
            {(blockGeom?.subtitleLines.length ? blockGeom.subtitleLines : [label.subtitle]).map((ln, li) => (
              <div key={li} style={{ whiteSpace: "nowrap" }}>{ln}</div>
            ))}
          </div>
        )}
      </div>
      {stepEdit?.canEdit && stepNodeId && onDragHandleDown && !isNote && (
        <span
          className="absolute top-0.5 left-0.5 z-10 cursor-grab select-none opacity-0 group-hover/cell:opacity-100 text-[11px] leading-none text-slate-500 hover:text-slate-200"
          title="Drag to reorder"
          aria-label="Drag to reorder"
          data-drag-handle=""
          onPointerDown={(e) => onDragHandleDown(stepNodeId, e)}
          onClick={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </span>
      )}
      {stepEdit && stepNodeId && (
        <span className="absolute top-0.5 right-0.5 z-10 opacity-0 group-hover/cell:opacity-100 focus-within:opacity-100">
          <ContextPad
            api={stepEdit}
            nodeId={stepNodeId}
            kind={!isNote && stepFrom && stepTo ? "call" : "other"}
            stepIndex={stepIndex ?? undefined}
            from={stepFrom}
            to={stepTo}
            {...(stepModifiers ? { modifiers: stepModifiers } : {})}
          />
        </span>
      )}
      {/* Tail-append affordance (P2 #20). A small NUB straddling the cell's
          BOTTOM edge (hover-revealed, edit mode only): press + drag away to
          spawn the next step. A live "+ new step" ghost + a connector stub show
          while dragging; the release opens the add-after input, which funnels
          through `addStepAfter(stepNodeId, text)` — the same OUTCOME as the
          connector "+". A tiny press (no drag) opens the input too. */}
      {appendable && stepNodeId && stepEdit && (
        <>
          <button
            type="button"
            data-append-nub=""
            aria-label="Drag to append the next step"
            title="Drag down to add the next step"
            // The nub owns its gesture — don't let the press bubble to the cell's
            // body-drag / select. `useAppendDrag` also preventDefaults + stops.
            onPointerDown={(e) => append.onAppendDown(e)}
            onClick={(e) => e.stopPropagation()}
            className={`absolute left-1/2 -bottom-1.5 z-20 flex h-3.5 w-3.5 -translate-x-1/2 cursor-grab select-none items-center justify-center rounded-full border border-sky-500/70 bg-slate-900 text-[10px] leading-none text-sky-300 transition-opacity hover:border-sky-400 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${append.active ? "opacity-100" : "opacity-0 group-hover/cell:opacity-100"}`}
          >
            +
          </button>
          {/* Live ghost — a dashed connector stub + a "+ new step" chip below the
              tail, shown once the drag crosses the threshold. Pointer-transparent
              so it never intercepts the in-flight pointer. */}
          {append.dragging && (
            <div
              data-append-ghost=""
              className="pointer-events-none absolute left-1/2 top-full z-10 flex -translate-x-1/2 flex-col items-center"
              aria-hidden
            >
              <div className="h-4 w-px border-l border-dashed border-sky-400/80" />
              <div className="whitespace-nowrap rounded border border-dashed border-sky-400/80 bg-slate-900/90 px-1.5 py-0.5 text-[10px] leading-none text-sky-300">
                + new step
              </div>
            </div>
          )}
          {/* The add-after input, anchored just below the tail — opened on
              release. Enter commits → `addStepAfter`; Escape / blur cancels. */}
          {append.adding && (
            <div
              data-append-input=""
              className="absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2"
              onClick={(e) => e.stopPropagation()}
            >
              <InlineEditInput
                initial=""
                placeholder="A > B"
                ariaLabel="Append step after"
                onCommit={(v) => {
                  stepEdit.addStepAfter(stepNodeId, v);
                  append.closeAdd();
                }}
                onCancel={() => append.closeAdd()}
                className="w-32 box-border rounded-sm border border-sky-500 bg-slate-950 px-1 py-0.5 text-[11px] text-slate-100 outline-none"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CommentCell({
  row,
  text,
  active,
  trigger,
  wait,
  laneIndex,
  topPad,
  bottomPad,
  blockGeom,
  onClick,
}: {
  row: Row;
  text: string;
  active: boolean;
  /** v0.9.0 §2.1: an ownerless leading await — still a START trigger. */
  trigger?: boolean;
  /** PROCESS.md §6: an ownerless mid-flow `await` wait (data attr only, no
   *  inline glyph — matching how an ownerless trigger is marked here). */
  wait?: boolean;
  laneIndex: (id: string) => number;
  topPad: number;
  bottomPad: number;
  /** Pure geometry — box + pre-wrapped lines (anchored and prose alike). */
  blockGeom?: SwimlaneBlockGeom;
  onClick: (e: ReactMouseEvent) => void;
}) {
  // Prose anchors in its POSITIONAL lane (the previous step's) and registers
  // as a flow node, so sequential arrows thread through it instead of being
  // drawn over the text. A pre-first-step note with no lane (the
  // `process:notes` sentinel) has nothing to anchor to — center it full-width.
  const toI = laneIndex(row.toLaneId);
  const anchored = toI >= 0;
  return (
    <div
      data-step-index={row.stepIndex ?? undefined}
      data-row-y={row.y}
      data-lane={anchored ? toI : undefined}
      data-comment="true"
      data-trigger={trigger ? "true" : undefined}
      data-wait={wait ? "true" : undefined}
      onClick={onClick}
      className="cursor-pointer flex items-center justify-center"
      style={{
        gridRow: row.y + 1,
        gridColumn: anchored ? toI + 1 : "1 / -1",
        padding: `${topPad}px ${anchored ? BLOCK_INSET : 24}px ${bottomPad}px`,
        // Unselected branch alternative — fade with its band's blocks.
        opacity: row.branchSelected === false ? 0.45 : 1,
      }}
    >
      <div
        className="text-center italic"
        style={{
          color: active ? "#fbbf24" : "#94a3b8",
          fontSize: 11,
          lineHeight: "14px",
          ...(anchored ? { width: "100%" } : { maxWidth: "70%" }),
          height: blockGeom?.rect.h,
          opacity: 0.85,
        }}
      >
        {(blockGeom?.lines.length ? blockGeom.lines : [text]).map((ln, li) => (
          <div key={li} style={{ whiteSpace: "nowrap" }}>{ln}</div>
        ))}
      </div>
    </div>
  );
}

export function TerminatorCell({
  row,
  laneIndex,
  nLanes,
  topPad,
  bottomPad,
  blockGeom,
  stepEdit,
}: {
  row: Row;
  laneIndex: (id: string) => number;
  nLanes: number;
  topPad: number;
  bottomPad: number;
  /** Pure geometry — pill height (width stays content-sized). */
  blockGeom?: SwimlaneBlockGeom;
  stepEdit?: ProcessStepEditApi;
}) {
  // Hooks run before the early return (rules of hooks); the `void` reads keep
  // the terminator narrowing local.
  const [editingReason, setEditingReason] = useState(false);
  if (!row.terminator) return null;
  const term = row.terminator;
  const idx = laneIndex(row.toLaneId);
  const col = idx < 0 ? 1 : idx + 1;
  void nLanes;
  const isFail = term.type === "fail";
  const all = term.all === true;
  const color = isFail ? "#ef4444" : "#10b981";
  // Double-click a terminator WITH a reason → inline-edit it (plan §4c). A
  // bodyless `fail`/`finish` (no reason) has no span to edit → stays read-only.
  const reasonEditable = !!stepEdit?.canEdit && term.reason !== undefined;
  return (
    <div
      style={{
        gridRow: row.y + 1,
        gridColumn: col,
        padding: `${topPad}px ${BLOCK_INSET}px ${bottomPad}px`,
        // Unselected branch alternative — fade with its band's blocks.
        opacity: row.branchSelected === false ? 0.45 : 1,
      }}
      className="flex items-center justify-center"
    >
      <div
        data-terminator=""
        {...(all ? { "data-terminator-all": "true" } : {})}
        title={
          all
            ? `${term.type} all — terminates every live branch of the process`
            : reasonEditable
              ? "double-click to edit the reason"
              : undefined
        }
        onDoubleClick={reasonEditable ? (e) => { e.stopPropagation(); setEditingReason(true); } : undefined}
        className="text-center inline-flex items-center justify-center gap-1.5 whitespace-nowrap shrink-0"
        style={{
          background: color,
          color: "#fff",
          borderRadius: 999,
          fontSize: 11,
          padding: "2px 10px",
          minWidth: 60,
          height: blockGeom?.rect.h,
          minHeight: blockGeom ? undefined : 15,
          // BPMN terminate-style double ring: the whole-process stop reads
          // differently from an ordinary branch end.
          ...(all ? { boxShadow: `0 0 0 2px rgb(var(--panel)), 0 0 0 3.5px ${color}` } : {}),
        }}
      >
        {editingReason ? (
          <InlineEditInput
            initial={term.reason ?? ""}
            ariaLabel="Edit terminator reason"
            className="w-24 box-border rounded-sm border border-white/70 bg-slate-900/90 px-1 text-[11px] text-white text-center outline-none"
            onCommit={(val) => {
              stepEdit?.setTerminatorText(term.nodeId, val);
              setEditingReason(false);
            }}
            onCancel={() => setEditingReason(false)}
          />
        ) : (
          <span>{term.reason ?? ""}</span>
        )}
        {all && (
          <span
            className="uppercase tracking-wide leading-none rounded border border-white/60"
            style={{ fontSize: 8, padding: "1.5px 3px" }}
          >
            all
          </span>
        )}
      </div>
    </div>
  );
}
